import React, { Component } from 'react';
import { Box, Button, Paper, Typography, Alert, AlertTitle, CircularProgress, Stack } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RefreshIcon from '@mui/icons-material/Refresh';
import { post } from '../util/api';
import { connect } from 'react-redux';

class PaddleBillingPaymentPage extends Component {
	state = {
		loading: true,
		error: null,
		success: false,
		priceId: null,
		paddleInitialized: false,
		login_user: this.props.user,
	};

	componentDidMount() {
		// URL'den price ID al: /paddle-billing/pri_xxxxx
		const pathParts = window.location.pathname.split('/');
		const priceId = pathParts[pathParts.length - 1];

		if (!priceId || !priceId.startsWith('pri_')) {
			this.setState({
				loading: false,
				error: 'Invalid price ID in URL. Expected format: pri_xxxxx',
			});
			return;
		}

		this.setState({ priceId }, () => {
			this.initializePaddle();
		});
	}

	initializePaddle = () => {
		// Mevcut Classic Paddle'ı geçici olarak sakla ve kaldır
		const classicPaddle = window.Paddle;
		delete window.Paddle;

		// Paddle.js v2 yükle
		const script = document.createElement('script');
		script.src = 'https://cdn.paddle.com/paddle/v2/paddle.js';
		script.async = true;
		script.onload = () => {
			// v2 yüklendi, şimdi PaddleBilling olarak sakla
			window.PaddleBilling = window.Paddle;
			// Classic'i geri koy (diğer sayfalar için)
			window.Paddle = classicPaddle;
			this.setupPaddle();
		};
		script.onerror = () => {
			// Hata durumunda Classic'i geri koy
			window.Paddle = classicPaddle;
			this.setState({
				loading: false,
				error: 'Failed to load payment system. Please refresh the page.',
			});
		};
		document.body.appendChild(script);
	};

	setupPaddle = () => {
		try {
			window.PaddleBilling.Setup({
				token: process.env.REACT_APP_PADDLE_BILLING_CLIENT_TOKEN,
			});

			this.setState({ paddleInitialized: true, loading: false }, () => {
				this.openCheckout();
			});
		} catch (err) {
			console.error('Paddle initialization error:', err);
			this.setState({
				loading: false,
				error: 'Failed to initialize payment system.',
			});
		}
	};

	openCheckout = () => {
		const { priceId, login_user } = this.state;

		post('/footprint/add', {
			category: 'maytapi',
			label: 'paddle-billing-checkout-open',
			extra: { priceId, login_user, type: 'india-upi' },
		}).catch(console.error);

		window.PaddleBilling.Checkout.open({
			items: [{ priceId: priceId, quantity: 1 }],
			...(login_user?.email && {
				customer: { email: login_user.email },
			}),
			customData: {
				userId: login_user?.id || null,
				source: 'india-upi-page',
			},
			successCallback: this.handleSuccess,
			closeCallback: this.handleClose,
		});
	};

	handleSuccess = (data) => {
		console.log('Payment success:', data);
		this.setState({ success: true, error: null });

		post('/footprint/add', {
			category: 'maytapi',
			label: 'paddle-billing-checkout-success',
			extra: {
				priceId: this.state.priceId,
				login_user: this.state.login_user,
				transactionId: data?.transaction?.id,
				customerId: data?.customer?.id,
				type: 'india-upi',
			},
		}).catch(console.error);
	};

	handleClose = () => {
		console.log('Checkout closed');
		if (!this.state.success) {
			post('/footprint/add', {
				category: 'maytapi',
				label: 'paddle-billing-checkout-close',
				extra: {
					priceId: this.state.priceId,
					login_user: this.state.login_user,
					type: 'india-upi',
				},
			}).catch(console.error);
		}
	};

	handleError = (event) => {
		console.error('Checkout error:', event);

		this.setState({
			error: event.detail || 'Payment failed. Please try again.',
		});

		// Footprint: hata
		post('/footprint/add', {
			category: 'maytapi',
			label: 'paddle-billing-checkout-error',
			extra: {
				priceId: this.state.priceId,
				login_user: this.state.login_user,
				error: event.detail,
				type: 'india-upi',
			},
		}).catch(console.error);
	};

	handleRetry = () => {
		this.setState({ error: null });
		this.openCheckout();
	};

	render() {
		const { loading, error, success, priceId } = this.state;

		if (loading) {
			return (
				<Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#fafafa' }}>
					<Paper sx={{ maxWidth: 500, p: 4, textAlign: 'center', boxShadow: 'rgb(131 131 131 / 65%) 0px 2px 8px 0px' }}>
						<CircularProgress size={60} sx={{ mb: 3, color: '#21ba45' }} />
						<Typography variant="h5" gutterBottom sx={{ fontWeight: 500 }}>
							Initializing Payment...
						</Typography>
						<Typography variant="body2" color="text.secondary">
							Please wait while we prepare your checkout
						</Typography>
					</Paper>
				</Box>
			);
		}

		if (success) {
			return (
				<Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#fafafa' }}>
					<Paper sx={{ maxWidth: 600, p: 4, textAlign: 'center', boxShadow: 'rgb(131 131 131 / 65%) 0px 2px 8px 0px' }}>
						<CheckCircleIcon sx={{ fontSize: 80, color: '#21ba45', mb: 2 }} />
						<Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
							Payment Successful!
						</Typography>
						<Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
							Thank you for your purchase. You will receive a confirmation email shortly.
						</Typography>
						<Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
							<AlertTitle>Next Steps</AlertTitle>
							Please contact our support team with your transaction details to activate your subscription.
						</Alert>
						<Button variant="contained" size="large" href="/" sx={{ bgcolor: '#21ba45', '&:hover': { bgcolor: '#1b9f3a' } }}>
							Go to Dashboard
						</Button>
					</Paper>
				</Box>
			);
		}

		return (
			<Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#fafafa' }}>
				<Paper sx={{ maxWidth: 600, p: 4, textAlign: 'center', boxShadow: 'rgb(131 131 131 / 65%) 0px 2px 8px 0px' }}>
					<img src="/maytapi.svg" alt="Maytapi" style={{ width: '10rem', marginBottom: '16px' }} />

					<Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
						Complete Your Purchase
					</Typography>

					<Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
						Price ID: {priceId}
					</Typography>

					{error && (
						<Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>
							<AlertTitle>Payment Failed</AlertTitle>
							{error}
						</Alert>
					)}

					<Typography variant="body1" color="text.secondary" gutterBottom>
						The payment window should open automatically.
					</Typography>
					<Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
						If it doesn't, please click the button below.
					</Typography>

					<Stack direction="row" spacing={2} justifyContent="center">
						<Button
							variant="contained"
							size="large"
							onClick={this.handleRetry}
							startIcon={<RefreshIcon />}
							sx={{ bgcolor: '#21ba45', '&:hover': { bgcolor: '#1b9f3a' } }}
						>
							Open Payment Window
						</Button>
						<Button variant="outlined" href="/" size="large">
							Back to Home
						</Button>
					</Stack>

					<Box sx={{ mt: 4, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
						<Typography variant="body2" color="text.secondary">
							🇮🇳 UPI payments are supported for customers in India
						</Typography>
					</Box>
				</Paper>

				<Box sx={{ position: 'fixed', bottom: 16, textAlign: 'center', width: '100%' }}>
					<Typography variant="body2" color="text.secondary">
						Having trouble? Contact us at{' '}
						<a href="mailto:info@maytapi.com" style={{ color: '#21ba45' }}>
							info@maytapi.com
						</a>
					</Typography>
				</Box>
			</Box>
		);
	}
}

const stateToProps = (state) => ({
	user: state.access.user.loggedUserObj,
});

export default connect(stateToProps)(PaddleBillingPaymentPage);
